import { Moment } from 'moment';
import {
  ComplementOriginEnum,
  CustomerType,
} from '../WorkOrders/WorkOrders.types';
import {
  InvoiceTaxpayerType,
  InvoiceType,
} from '../../invoice/invoices/invoices.enums';
import {
  WorkOrderItemOrigin,
  WorkOrderItemType,
} from '../WorkOrdersItems/WorkOrdersItems.types';
import { PersonType } from '../../people/Customers/Customers.types';
import {
  WorkOrderComplementActivityStatus,
  WorkOrderTag,
} from '../WorkOrderTags/WorkOrdersTags.types';
import { PageRequest } from '@/core/request/PageRequest';
import {
  ComplementActionOrigin,
  ComplementActionType,
} from './WorkOrdersComplements.enums';

export enum WorkOrderComplementTypeNumber {
  Courtesy = '0',
  Warranty = '1',
  WorkOrder = '2',
  TotalLoss = '3',
  Return = '4',
  Revision = '5',
  PortoBattery = '6',
}

export enum WorkOrderComplementType {
  Courtesy = 'domain.enums.work_order_type.courtesy',
  Warranty = 'domain.enums.work_order_type.warranty',
  WorkOrder = 'domain.enums.work_order_type.work_order',
  TotalLoss = 'domain.enums.work_order_type.total_loss',
  Return = 'domain.enums.work_order_type.return',
  Revision = 'domain.enums.work_order_type.revision',
}

export enum WorkOrderComplementStatusNumber {
  Open = '0',
  Authorized = '1',
  Closed = '2',
  Cancelled = '3',
}

export enum WorkOrderComplementCloseType {
  one = 'one',
  all = 'all',
}

export enum WorkOrderComplementStatus {
  Open = 'domain.enums.work_order_status.open',
  Authorized = 'domain.enums.work_order_status.authorized',
  Closed = 'domain.enums.work_order_status.closed',
  Cancelled = 'domain.enums.work_order_status.cancelled',
}

export enum WorkOrderComplementTankLevel {
  Full = 'domain.enums.tank_level.full',
  SevenEights = 'domain.enums.tank_level.seven_eights',
  ThreeQuarters = 'domain.enums.tank_level.three_quarters',
  Half = 'domain.enums.tank_level.half',
  Quarter = 'domain.enums.tank_level.quarter',
  OneEight = 'domain.enums.tank_level.one_eight',
  Reserve = 'domain.enums.tank_level.reserve',
  Empty = 'domain.enums.tank_level.empty',
}

export const workorderComplementColor = {
  [WorkOrderComplementStatus.Open]: 'blue',
  [WorkOrderComplementStatus.Authorized]: 'yellow',
  [WorkOrderComplementStatus.Cancelled]: 'red',
  [WorkOrderComplementStatus.Closed]: 'green',
};

export const workorderComplementLabel = {
  [WorkOrderComplementStatus.Open]: 'Aberto',
  [WorkOrderComplementStatus.Authorized]: 'Autorizado',
  [WorkOrderComplementStatus.Cancelled]: 'Cancelado',
  [WorkOrderComplementStatus.Closed]: 'Fechado',
};

export enum ComplementPaymentMethod {
  CashPayment = 'domain.enums.work_order_porto_payment_method.cash_payment',
  CreditCard = 'domain.enums.work_order_porto_payment_method.credit_card',
  PortoSeguroCreditCard = 'domain.enums.work_order_porto_payment_method.porto_seguro_credit_card',
  Warranty = 'domain.enums.work_order_porto_payment_method.warranty',
  DirectDebitfromCheckingAccount = 'domain.enums.work_order_porto_payment_method.direct_debitfrom_checking_account',
  FirstPurchaseWithPortoSeguroCard = 'domain.enums.work_order_porto_payment_method.first_purchase_with_porto_seguro_card',
}

export const complementPaymentMethodLabel: Record<
  ComplementPaymentMethod,
  string
> = {
  'domain.enums.work_order_porto_payment_method.cash_payment': 'Dinheiro',
  'domain.enums.work_order_porto_payment_method.credit_card':
    'Cartão de crédito',
  'domain.enums.work_order_porto_payment_method.direct_debitfrom_checking_account':
    'Débito direto em conta corrente',
  'domain.enums.work_order_porto_payment_method.first_purchase_with_porto_seguro_card':
    'primeira compra com cartão porto seguro',
  'domain.enums.work_order_porto_payment_method.porto_seguro_credit_card':
    'Cartão de crédito porto seguro',
  'domain.enums.work_order_porto_payment_method.warranty': 'Garantia',
};

export interface WorkOrderComplement {
  id: number;
  number: number;
  complementIdentifier: string;
  estimator: {
    id: number;
    name: string;
  };
  customerType: CustomerType;
  type: WorkOrderComplementType;
  insurer?: {
    id: number;
    name: string;
  };
  status: WorkOrderComplementStatus;
  accidentNumber: string;
  insurerDeductibleAmount: number;
  entryDate: string;
  authorizationDate: string;
  closingDate: string;
  companyCode?: string;
  estimatedLeaveDate: string;
  returnDate: string;
  prisma: string;
  currentKilometers: number;
  tankLevel: WorkOrderComplementTankLevel;
  workOrderGuid: string;
  ssLocaliza: string | null;
  reasonEstimatedLeaveDate: string | null;
  authorizedSignature?: string;
  totalItems?: number;
  workOrderTag?: WorkOrderTag;
  activityStatusModel?: WorkOrderComplementActivityStatus;
  activityId?: string;
  pecaAiBasketId?: string;
  pecaAiOrderId?: string;
  policyNumber?: string;
  womBasketId?: string;
  lastOilChange?: string;
  lastOilChangeKilometers?: number;
  hasBenefitItem?: boolean;
  origin?: ComplementOriginEnum;
  indicatedBy?: string;
  contact?: string;
  portoBatteryAuthorizationCode?: string;
  portoBatteryPaymentMethod?: ComplementPaymentMethod;
  portoBatteryInstallmentsQuantity?: number;
  scheduleDateTime?: string;
  scheduleEndDateTime?: string;
  receptionDateTime?: string;
  hasJobCard?: boolean;
  zfBasketToken?: string;
  winchCode?: string;
}

export interface UpdateWorkOrderComplementRequest {
  workOrderId: number;
  complementId: number;
  companyId: number;
  estimatorId: number;
  customerType: CustomerType;
  type: WorkOrderComplementType;
  insurerId?: number;
  accidentNumber?: string;
  insurerDeductibleAmount?: number;
  prisma?: string;
  currentKilometers?: number;
  tankLevel?: WorkOrderComplementTankLevel;
  entryDate?: string;
  closingDate?: string;
  authorizationDate?: string;
  estimatedLeaveDate?: string;
  returnDate?: string;
  situationId?: number;
  indicatedBy?: string;
  contact?: string;
}

export interface CreateWorkOrderComplementRequest {
  workOrderId: number;
  complementId: number;
  customerType: CustomerType;
  type: WorkOrderComplementType;
  insurerId?: number;
  accidentNumber?: string;
  insurerDeductibleAmount?: number;
  prisma?: string;
  currentKilometers?: number;
  tankLevel?: WorkOrderComplementTankLevel;
  entryDate?: string;
  estimatedLeaveDate?: string;
}

export interface ComplementCloseFormValues {
  hasReturnDate?: boolean;
  closingDate?: Moment;
}

export interface WorkorderActionsRequest {
  workorderId: number;
  complementId: number;
  setReturnDate?: boolean;
  closingDate?: string;
  authorizedSignature?: string | null;
}

export interface CloseAllWorkOrderComplementRequest {
  setReturnDate?: boolean;
  closingDate?: string;
  workOrderId?: number;
}

export interface WorkorderCancelRequest {
  workorderId: number;
  complementId: number;
  cancellationReason: string;
}

export interface WorkOrderPendingItem {
  description: string;
  quantity: number;
}

export interface WorkOrderCloseVerifyResponse {
  generateFinancial: boolean;
  pendingPurchase?: WorkOrderPendingItem[];
  pendingDeliveries?: WorkOrderPendingItem[];
}

export interface WorkOrderCloseAllVerifyResponse {
  generateFinancial: boolean;
  pendingPurchase?: WorkOrderPendingItem[];
  pendingDeliveries?: WorkOrderPendingItem[];
}

export interface WorkOrderCloseVerifyRequest {
  workorderId: number;
  complementId?: number;
}

export interface WorkOrderUpdateItemsRequest {
  workorderId: number;
  complementId?: number;
}

export interface WorkOrderUpdateEstimatedDateRequest {
  workorderId: number;
  complementId: number;
  estimatedLeaveDate: string;
  reason: string;
}

export interface WorkOrderUpdateEstimatedDateFormValues {
  estimatedLeaveDateModal: Moment;
  reasonModal: string;
}

export interface WorkOrderComplementInvoiceValuesRequest {
  workorderId?: number;
  complementsIds: number[];
  model: number;
}

export enum InvoiceValuesItemType {
  Part = 'domain.enums.nf_complement_item_info_type.part',
  Service = 'domain.enums.nf_complement_item_info_type.service',
}

export const InvoiceValuesTypes = {
  [InvoiceValuesItemType.Part]: 'Peça',
  [InvoiceValuesItemType.Service]: 'Serviço',
};

export const InvoiceEnumValuesTypes = {
  [InvoiceType.NFC]: 'Peça',
  [InvoiceType.NFE]: 'Peça',
  [InvoiceType.NFS]: 'Serviço',
  [InvoiceType.CFE]: 'Cupom Fiscal',
};

export const InvalidFieldsTranslation = {
  ['prisma']: 'Prisma',
  ['currentKilometers']: 'Km atual',
  ['tankLevel']: 'Nível do tanque',
};

export const InvoiceEnumTypes = {
  [InvoiceType.NFE]: InvoiceValuesItemType.Part,
  [InvoiceType.NFC]: InvoiceValuesItemType.Part,
  [InvoiceType.NFS]: InvoiceValuesItemType.Service,
};

export interface ClientValues {
  id: number;
  name: string;
  cpfCnpj?: string;
  personType: PersonType;
  taxpayerType?: InvoiceTaxpayerType;
}

export interface InvoiceValuesItems {
  description: string;
  id: number;
  unitPrice: number;
  type: string;
  quantity: number;
  valueDiscount: number;
  workOrderComplementNumber: number;
  workOrderComplementId: number;
  subAreaName?: string;
  isInternal?: boolean;
}

export interface InvoiceValuesItem {
  client: ClientValues;
  type: InvoiceValuesItemType;
  model: number;
  serie: string;
  items: InvoiceValuesItems[];
}

export interface WorkOrderComplementInvoiceValues {
  particular?: {
    services?: InvoiceValuesItem;
    parts?: InvoiceValuesItem;
  };
  insurer?: {
    services?: InvoiceValuesItem;
    parts?: InvoiceValuesItem;
  };
  franchise?: {
    services?: InvoiceValuesItem;
    parts?: InvoiceValuesItem;
  };
  porto?: {
    services?: InvoiceValuesItem;
    parts?: InvoiceValuesItem;
  };
}

export interface WorkOrderComplementIncludeTagRequest {
  workorderId: number;
  complementId: number;
  tagId?: number | null;
}

export interface WorkOrderComplementChangeStatusRequest {
  workorderId: number;
  complementId: number;
  statusId?: string | null;
}

export interface WorkOrderComplementSignaturePublic {
  complementGuid: string;
  signature: string;
  authorizeComplement: boolean;
}

export interface WorkOrderComplementGenerateActivity {
  workOrderId: number;
  complementId: number;
}

export interface WorkOrderComplementBindWithPecaAiParams {
  workOrderId: number;
  complementId: number;
  description: string;
  products: {
    productId: string;
    amount: number;
  }[];
}

export interface WorkOrderComplementSyncWithPecaAiParams {
  workOrderId: number;
  complementId: number;
}

export interface WorkOrderComplementSyncWithWom {
  workOrderId: number;
  complementId: number;
}

export interface WorkOrderComplementBindItemsWithOperational {
  workOrderId: number;
  complementId: number;
}

export interface WorkOrderComplementCreatePecaAiOrderRequest {
  workOrderId: number;
  complementId: number;
}

export interface WorkOrderComplementItemsHitoryRequest {
  workOrderId?: number;
  complementId?: number;
  nextToken?: number;
  rows?: number;
  order?: number;
}

export interface WorkOrderComplementItemHistory {
  id: number;
  workOrderComplementId: number;
  partId: number;
  itemDescription: string;
  serviceId: number;
  subAreaName: string;
  supplierName: string;
  employeeName: string;
  manufacturerName: string;
  manufacturingCode: string;
  type: WorkOrderItemType;
  isInternal: boolean;
  quantity: number;
  unitPrice: number;
  discount: number;
  unitCost: number;
  tag?: {
    id: number;
    name: string;
    color: string;
  };
  isPaintedPart: boolean;
  kitId: number;
  kitName: string;
  origin: WorkOrderItemOrigin;
}

export interface WorkOrderComplementItemHistoryResponse {
  nextToken?: number;
  data: WorkOrderComplementItemHistory[];
}

export interface WorkOrderComplementCreatePecaAiOrder {
  complementId: number;
  workOrderId: number;
}

export interface WorkOrderComplementSyncWithMyBCSRequest {
  workOrderId: number;
  complementId: number;
}

export interface WorkOrderComplementEditScheduleRequest {
  workOrderId: number;
  complementId: number;
  scheduleDateTime: string;
  scheduleEndDateTime: string;
  receptionDateTime?: string;
}

export interface WorkOrderComplementListStatusActionRequest
  extends PageRequest {
  workOrderId?: number;
  complementId?: number;
}

export interface WorkOrderComplementListStatusAction {
  id: number;
  origin?: ComplementActionOrigin;
  action: ComplementActionType;
  fromStatus?: WorkOrderComplementStatus;
  userId: number;
  userName: string;
  userImageUrl?: string;
  complementId: number;
  workOrderId: number;
  actionAt: string;
}

export interface WorkOrderComplementListStatusActionResponse {
  nextToken?: number;
  data: WorkOrderComplementListStatusAction[];
}
